// Shared multi-format grid export — mirrors the DevExpress "Export to" dropdown
// in AlpacaLims_GoLive (CSV · HTML · MHT · Text · PDF · RTF · XLS · XLSX · DOCX).
// Every popup grid (TabGrid, ProspectChildGrid) uses this so the option set is
// identical everywhere. Heavy writers (xlsx / jspdf / docx) are dynamically
// imported so they only load when that format is actually chosen.

export type ExportFormat = 'csv' | 'html' | 'mht' | 'txt' | 'pdf' | 'rtf' | 'xls' | 'xlsx' | 'docx';

export interface ExportFormatDef { key: ExportFormat; label: string; icon: string; }

/** The dropdown items, in the same order GoLive lists them (Image omitted — it
 *  needs a DOM raster of the grid; tracked separately). */
export const EXPORT_FORMATS: ExportFormatDef[] = [
  { key: 'csv',  label: 'CSV File',  icon: 'ti-file-type-csv' },
  { key: 'html', label: 'HTML File', icon: 'ti-file-type-html' },
  { key: 'mht',  label: 'MHT File',  icon: 'ti-file-code' },
  { key: 'txt',  label: 'Text File', icon: 'ti-file-type-txt' },
  { key: 'pdf',  label: 'PDF File',  icon: 'ti-file-type-pdf' },
  { key: 'rtf',  label: 'RTF File',  icon: 'ti-file-text' },
  { key: 'xls',  label: 'XLS File',  icon: 'ti-file-type-xls' },
  { key: 'xlsx', label: 'XLSX File', icon: 'ti-file-spreadsheet' },
  { key: 'docx', label: 'DOCX File', icon: 'ti-file-type-doc' },
];

const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escRtf  = (s: string) => s.replace(/\\/g, '\\\\').replace(/\{/g, '\\{').replace(/\}/g, '\\}')
  // Non-ASCII → \uNNNN? escapes so accents survive.
  .replace(/[\u0080-\uffff]/g, m => `\\u${m.charCodeAt(0)}?`);

function saveBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1500);
}

function buildHtmlTable(headers: string[], rows: string[][]): string {
  const th = headers.map(h => `<th style="border:1px solid #888;padding:4px 6px;background:#efefef;text-align:left">${escHtml(h)}</th>`).join('');
  const trs = rows.map(r => `<tr>${r.map(c => `<td style="border:1px solid #888;padding:4px 6px">${escHtml(c)}</td>`).join('')}</tr>`).join('');
  return `<table style="border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif;font-size:12px">`
    + `<thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`;
}

function buildHtmlDoc(title: string, table: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(title)}</title></head><body>${table}</body></html>`;
}

/**
 * Export the given rows in the chosen format. `headers` are the column captions;
 * `rows` are already-stringified cell values in the same column order (the caller
 * applies whatever filtering is on screen — "export exactly what you see").
 */
export async function exportRows(format: ExportFormat, baseName: string, headers: string[], rows: string[][]): Promise<void> {
  switch (format) {
    case 'csv': {
      const q = (s: string) => `"${s.replace(/"/g, '""')}"`;
      const csv = [headers.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\r\n');
      saveBlob(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`);
      return;
    }
    case 'txt': {
      const txt = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\r\n');
      saveBlob(new Blob(['\uFEFF' + txt], { type: 'text/plain;charset=utf-8;' }), `${baseName}.txt`);
      return;
    }
    case 'html': {
      const html = buildHtmlDoc(baseName, buildHtmlTable(headers, rows));
      saveBlob(new Blob([html], { type: 'text/html;charset=utf-8;' }), `${baseName}.html`);
      return;
    }
    case 'mht': {
      const html = buildHtmlDoc(baseName, buildHtmlTable(headers, rows));
      const boundary = '----=_NextPart_AI_Export';
      const mht = [
        'MIME-Version: 1.0',
        `Content-Type: multipart/related; boundary="${boundary}"`,
        '',
        `--${boundary}`,
        'Content-Type: text/html; charset="utf-8"',
        'Content-Transfer-Encoding: 8bit',
        'Content-Location: file:///C:/export.htm',
        '',
        html,
        `--${boundary}--`,
        '',
      ].join('\r\n');
      saveBlob(new Blob([mht], { type: 'message/rfc822' }), `${baseName}.mht`);
      return;
    }
    case 'xls': {
      // Excel opens an HTML table saved as .xls — the classic, dependency-free
      // route that avoids BIFF quirks (XLSX below uses the real writer).
      const html = buildHtmlDoc(baseName, buildHtmlTable(headers, rows));
      saveBlob(new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8;' }), `${baseName}.xls`);
      return;
    }
    case 'xlsx': {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
      saveBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${baseName}.xlsx`);
      return;
    }
    case 'pdf': {
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: headers.length > 6 ? 'landscape' : 'portrait', unit: 'pt' });
      autoTable(doc, {
        head: [headers], body: rows, styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [232, 103, 10], textColor: 255 }, margin: { top: 28, left: 20, right: 20 },
      });
      doc.save(`${baseName}.pdf`);
      return;
    }
    case 'rtf': {
      const line = (cells: string[], bold: boolean) =>
        (bold ? '{\\b ' : '') + cells.map(escRtf).join('\\tab ') + (bold ? '}' : '') + '\\par';
      const body = [line(headers, true), ...rows.map(r => line(r, false))].join('\n');
      const rtf = `{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Segoe UI;}}\\fs18\n${body}\n}`;
      saveBlob(new Blob([rtf], { type: 'application/rtf' }), `${baseName}.rtf`);
      return;
    }
    case 'docx': {
      const docx = await import('docx');
      const { Document, Packer, Table, TableRow, TableCell, Paragraph, TextRun, WidthType } = docx;
      const cell = (text: string, bold: boolean) => new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text, bold })] })],
      });
      const table = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({ tableHeader: true, children: headers.map(h => cell(h, true)) }),
          ...rows.map(r => new TableRow({ children: r.map(c => cell(c, false)) })),
        ],
      });
      const doc = new Document({ sections: [{ children: [table] }] });
      const blob = await Packer.toBlob(doc);
      saveBlob(blob, `${baseName}.docx`);
      return;
    }
  }
}
